import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, EmptyState, Spinner, ViewTab, ViewTabList } from '@wundo/ui';
import { listUserMemories } from '@/lib/users.ts';

export interface MemoryListProps {
  userId: string;
  /**
   * The memories the agent was handed on the turn being looked at. Given, the
   * list opens on those — which of them the model actually saw is the question
   * a transcript is being read for.
   */
  offeredHandles?: string[];
}

type MemoryFilter = 'offered' | 'all';

/** What Wundo remembers about this person, most recently updated first. */
export function MemoryList({ userId, offeredHandles = [] }: MemoryListProps) {
  const hasOffered = offeredHandles.length > 0;
  const [filter, setFilter] = useState<MemoryFilter>(hasOffered ? 'offered' : 'all');

  const {
    data: memories,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['users', userId, 'memories'],
    queryFn: () => listUserMemories(userId),
  });

  const all = memories ?? [];
  const offered = new Set(offeredHandles);
  const visible =
    filter === 'offered' ? all.filter((memory) => offered.has(memory.name)) : all;

  return (
    <div className='flex flex-col gap-3'>
      {hasOffered && (
        <ViewTabList aria-label='Memory filter'>
          <ViewTab active={filter === 'offered'} onClick={() => setFilter('offered')}>
            {`Offered this turn (${offeredHandles.length})`}
          </ViewTab>
          <ViewTab active={filter === 'all'} onClick={() => setFilter('all')}>
            {`All (${all.length})`}
          </ViewTab>
        </ViewTabList>
      )}

      {isPending && (
        <p className='flex justify-center py-6'>
          <Spinner />
        </p>
      )}

      {isError && (
        <p className='py-6 text-center text-sm text-danger'>Failed to load memories</p>
      )}

      {!isPending && !isError && visible.length === 0 && (
        <Card>
          <EmptyState
            title={
              filter === 'offered' ? 'No memories offered this turn.' : 'No memories yet.'
            }
          />
        </Card>
      )}

      {visible.length > 0 && (
        <ul className='flex max-h-[60vh] flex-col gap-2 overflow-y-auto'>
          {visible.map((memory) => (
            <li key={memory.id} className='rounded-md border border-line bg-surface p-3'>
              <p className='text-sm font-semibold text-ink'>{memory.description}</p>
              <p className='mt-1 text-sm break-words whitespace-pre-wrap text-ink-secondary'>
                {memory.content}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
